import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useEffect, useState, useMemo } from "react";
import useEmployeeSelection from "../../../hooks/useEmployeeSelection";
import Iconify from "../../../components/Icon/Iconify";
import { RHFMultiCheckbox } from "../../../components/RFH/RFHCheckbox";
import { FormProvider, useForm } from "react-hook-form";
import DropdownPopover from "../../../components/DropDownPopover/DropDownPopover";
import AllEmployeeReportQuery from "../../../utils/reactQuery/manager/employeeDetail/AllEmployeeReport";
import ManagersListQuery from "../../../utils/reactQuery/ManagersList";
import { RHFDropdownSelect } from "../../../components/RFH/RFHselect";
import EmployeeTable from "./EmployeeTable";
import {
  avgWorkHoursOptions,
  employeeStatusOptions,
  teamOptions,
} from "../../../utils/constants";

// Helper function to get display label for selected values
const getDisplayLabel = (
  selectedValues,
  options,
  defaultLabel,
  isMultiSelect = false
) => {
  if (
    !selectedValues ||
    (Array.isArray(selectedValues) && selectedValues.length === 0)
  ) {
    return defaultLabel;
  }

  if (isMultiSelect) {
    const selectedItems = options.filter((option) =>
      selectedValues.includes(option.value)
    );
    if (selectedItems.length === 0) return defaultLabel;
    if (selectedItems.length === 1) return selectedItems[0].label;
    if (selectedItems.length === options.length) return `All ${defaultLabel}s`;
    if (selectedItems.length <= 2) {
      return selectedItems.map((item) => item.label).join(", ");
    }
    return `${selectedItems.length} ${defaultLabel}s selected`;
  } else {
    const selectedItem = options.find(
      (option) => option.value === selectedValues
    );
    return selectedItem ? selectedItem.label : defaultLabel;
  }
};

// Helper function to get detailed selected values for tooltips or detailed display
const getSelectedValuesDetails = (
  selectedValues,
  options,
  isMultiSelect = false
) => {
  if (
    !selectedValues ||
    (Array.isArray(selectedValues) && selectedValues.length === 0)
  ) {
    return [];
  }

  if (isMultiSelect) {
    return options.filter((option) => selectedValues.includes(option.value));
  } else {
    const selectedItem = options.find(
      (option) => option.value === selectedValues
    );
    return selectedItem ? [selectedItem] : [];
  }
};

export default function AllEmployees() {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState([]);
  const [selectedManager, setSelectedManager] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedWorkHours, setSelectedWorkHours] = useState("");
  const [selectedBreakCount, setSelectedBreakCount] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");

  // console.log("selectedEmployee", selectedEmployee);
  // console.log("selectedTeam", selectedTeam);
  // console.log("selectedManager", selectedManager);
  // console.log("selectedStatus", selectedStatus);
  // console.log("selectedWorkHours", selectedWorkHours);
  // console.log("selectedBreakCount", selectedBreakCount);
  // console.log("selectedStartDate", selectedStartDate);
  // console.log("selectedEndDate", selectedEndDate);

  const { emplist } = useEmployeeSelection(selectedStatus);
  // console.log("AllEmployees emplist", emplist);

  const { managersList } = ManagersListQuery();
  // console.log("managersList", managersList);

  const { report, isLoading } = AllEmployeeReportQuery(
    selectedEmployee?.employee_id || "all",
    selectedTeam,
    selectedManager,
    selectedStatus,
    selectedWorkHours,
    selectedStartDate,
    selectedEndDate,
    selectedBreakCount
  );

  const handleChange = (event, newValue) => {
    setSelectedEmployee(newValue);
  };

  const defaultValues = {
    team: [],
    manager: [],
    status: "Active",
    workingHours: "all",
  };

  const methods = useForm({
    defaultValues,
  });

  const { watch } = methods;

  // Watch specific fields for changes
  const teamValue = watch("team");
  const managerValue = watch("manager");
  const statusValue = watch("status");
  const workingHoursValue = watch("workingHours");

  // Sync form values with state
  useEffect(() => {
    setSelectedTeam(teamValue || []);
  }, [teamValue]);

  useEffect(() => {
    setSelectedManager(managerValue || []);
  }, [managerValue]);

  useEffect(() => {
    setSelectedStatus(statusValue || "");
  }, [statusValue]);

  useEffect(() => {
    // timeExtractor(workingHoursValue);
    setSelectedWorkHours(workingHoursValue || "");
  }, [workingHoursValue]);

  // Compute button labels for dropdowns
  const teamButtonLabel = useMemo(
    () => getDisplayLabel(teamValue, teamOptions, "Team", true),
    [teamValue]
  );

  const managerButtonLabel = useMemo(
    () => getDisplayLabel(managerValue, managersList || [], "Manager", true),
    [managerValue, managersList]
  );

  const statusButtonLabel = useMemo(
    () => getDisplayLabel(statusValue, employeeStatusOptions, "Status", false),
    [statusValue]
  );

  const workHoursButtonLabel = useMemo(
    () =>
      getDisplayLabel(
        workingHoursValue,
        avgWorkHoursOptions,
        "Avg. Working Hours",
        false
      ),
    [workingHoursValue]
  );

  // Compute tooltip content for detailed view
  const teamTooltipContent = useMemo(() => {
    const selectedItems = getSelectedValuesDetails(
      teamValue,
      teamOptions,
      true
    );
    return selectedItems.length > 0
      ? `Selected: ${selectedItems.map((item) => item.label).join(", ")}`
      : null;
  }, [teamValue]);

  const managerTooltipContent = useMemo(() => {
    const selectedItems = getSelectedValuesDetails(
      managerValue,
      managersList || [],
      true
    );
    return selectedItems.length > 0
      ? `Selected: ${selectedItems.map((item) => item.label).join(", ")}`
      : null;
  }, [managerValue, managersList]);

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            paddingX={4}
            paddingY={2}
          >
            <Box width={350}>
              <Autocomplete
                options={emplist}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    variant="outlined"
                    placeholder="Search..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <Iconify
                          icon="ph:magnifying-glass-bold"
                          color="#B9B9B9"
                        />
                      ),
                    }}
                  />
                )}
                value={selectedEmployee}
                onChange={handleChange}
                isOptionEqualToValue={(option, value) =>
                  option.value === value?.employee_id
                }
                fullWidth
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <DropdownPopover
                buttonLabel={teamButtonLabel}
                tooltipContent={teamTooltipContent}
              >
                <RHFMultiCheckbox name="team" options={teamOptions || []} />
              </DropdownPopover>
              <DropdownPopover
                buttonLabel={managerButtonLabel}
                tooltipContent={managerTooltipContent}
                width={250}
              >
                <RHFMultiCheckbox name="manager" options={managersList || []} />
              </DropdownPopover>
              <RHFDropdownSelect name="status" buttonLabel={statusButtonLabel}>
                {employeeStatusOptions.map((opt, idx) => (
                  <MenuItem value={opt.value} key={idx}>
                    {opt.label}
                  </MenuItem>
                ))}
              </RHFDropdownSelect>
              <RHFDropdownSelect
                name="workingHours"
                buttonLabel={workHoursButtonLabel}
                isTimer={true}
              >
                {avgWorkHoursOptions.map((opt, idx) => (
                  <MenuItem key={idx} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </RHFDropdownSelect>
            </Box>
          </Box>
        </form>
      </FormProvider>

      <EmployeeTable
        report={report}
        isLoading={isLoading}
        selectedBreakCount={selectedBreakCount}
        setSelectedBreakCount={setSelectedBreakCount}
        selectedStartDate={selectedStartDate}
        setSelectedStartDate={setSelectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedEndDate={setSelectedEndDate}
      />
    </>
  );
}
